import React from 'react'
import { IconHeartHandshake, IconPlus, IconRocket } from '@tabler/icons-react'

import Text from 'components/ui/text'
import { cn } from 'utils/classMerge'
import { ButtonCTA } from 'components/ui/button-cta'
import { translate } from 'utils/translation'

import NPDigitalLogo from '../../shared/assets/np-digital-logo'
import { useLeadCaptureStore } from '../store/lead-capture-store'

import { CustomersSection } from './customers-section'

interface HireServicePathProps {
  className?: string
  onClickStrategicServices?: () => void
  onClickFullService?: () => void
}

interface HireServiceContentProps {
  title: string
  subtitle: string
  description: string
  ctaText: string
  icon: React.ReactNode
  onClick: () => void
}

const HireServiceContent = ({
  title,
  subtitle,
  description,
  ctaText,
  icon,
  onClick,
}: HireServiceContentProps) => {
  const { isLoading } = useLeadCaptureStore()

  return (
    <div className="tw-flex tw-flex-col tw-gap-6 tw-text-left md:tw-gap-8 lg:tw-max-w-[50%]">
      <div className="tw-flex tw-items-center tw-justify-start tw-gap-3">
        {icon}
        <IconPlus stroke={2} size={24} className="tw-text-zinc-400" />
        <NPDigitalLogo className="tw-h-8 tw-w-20" />
      </div>

      <div className="tw-flex tw-flex-col tw-gap-6 md:tw-gap-8">
        <div className="tw-flex tw-flex-col">
          <Text
            as="h3"
            className="tw-m-0 tw-font-geomanist tw-text-2xl tw-font-bold tw-text-zinc-900"
          >
            {title}
          </Text>
          <Text className="tw-m-0 tw-text-base tw-font-medium tw-text-zinc-700">
            {subtitle}
          </Text>
        </div>

        <ButtonCTA
          size="lg"
          onClick={onClick}
          className="tw-h-fit tw-w-full tw-font-bold tw-text-white tw-shadow-lg"
          textClassName="tw-text-wrap"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {translate(ctaText)}
        </ButtonCTA>

        <Text className="tw-m-0 tw-text-sm tw-text-zinc-500 md:tw-text-base">
          {description}
        </Text>
      </div>
    </div>
  )
}

export const HireServicePath = ({
  onClickStrategicServices,
  onClickFullService,
  className,
}: HireServicePathProps) => {
  const handleStrategicServicesClick = () => {
    onClickStrategicServices?.()
  }

  const handleFullServiceClick = () => {
    onClickFullService?.()
  }

  return (
    <div
      className={cn(
        className,
        'tw-flex tw-flex-col tw-gap-6 tw-rounded-2xl tw-border tw-border-solid tw-border-zinc-200 tw-bg-white tw-p-6 md:tw-gap-8 md:tw-p-8',
      )}
    >
      <div
        className={cn(
          className,
          'tw-flex tw-flex-col tw-justify-between tw-gap-6 md:tw-flex-row md:tw-gap-8',
        )}
      >
        <HireServiceContent
          icon={
            <IconHeartHandshake
              size={48}
              stroke={1.5}
              className="tw-text-orange-500"
            />
          }
          title="onboarding_questionnaire_step7_with_you"
          subtitle="onboarding_questionnaire_step7_with_you_subheading"
          description="onboarding_questionnaire_step7_with_you_brief"
          ctaText="onboarding_questionnaire_step7_with_you_button"
          onClick={handleStrategicServicesClick}
        />

        <div className="tw-hidden tw-h-full tw-w-[1px] tw-bg-zinc-200 md:tw-block" />
        <div className="tw-block tw-h-[1px] tw-w-full tw-bg-zinc-200 md:tw-hidden" />

        <HireServiceContent
          icon={
            <IconRocket size={48} stroke={1.5} className="tw-text-orange-500" />
          }
          title="onboarding_questionnaire_step7_for_you"
          subtitle="onboarding_questionnaire_step7_for_you_subheading"
          description="onboarding_questionnaire_step7_for_you_brief"
          ctaText="onboarding_questionnaire_step7_for_you_button"
          onClick={handleFullServiceClick}
        />
      </div>

      <CustomersSection />
    </div>
  )
}
